import { useCallback, type ReactNode } from "react";
import { Badge, Box, Text } from "@salestrak/ui";

import { EntityGroupTag, EntityTag, hasEntityPreview } from "#app/components/entity";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import type { EntityData } from "#app/lib/entity-visuals";
import type { FormItem, FormProperty } from "#app/lib/forms";

export function formOptionProperty(item: FormItem): FormProperty | null {
  return item.kind === "property" ? item.property : null;
}

/** Null for non-entity options and for an id the workspace no longer holds. */
export function useFormOptionEntity(
  property: FormProperty | null,
): (value: string) => EntityData | null {
  const { issueSourceMap, issueTypeMap, stageMap, teamMap, workflowMap } = useWorkspaceData();
  return useCallback(
    (value: string): EntityData | null => {
      switch (property) {
        case "issueSource": {
          const issueSource = issueSourceMap.get(value);
          return issueSource ? { id: value, issueSource, kind: "issueSource" } : null;
        }
        case "issueType": {
          const issueType = issueTypeMap.get(value);
          return issueType ? { id: value, issueType, kind: "issueType" } : null;
        }
        case "stage": {
          const stage = stageMap.get(value);
          return stage
            ? { id: value, kind: "stage", stage, workflowId: stage.workflowId ?? undefined }
            : null;
        }
        case "teams": {
          const team = teamMap.get(value);
          return team ? { id: value, kind: "team", team } : null;
        }
        case "workflow": {
          const workflow = workflowMap.get(value);
          return workflow ? { id: value, kind: "workflow", workflow } : null;
        }
        default:
          return null;
      }
    },
    [issueSourceMap, issueTypeMap, property, stageMap, teamMap, workflowMap],
  );
}

export function FormOptionLabel({
  entity,
  label,
  truncate = false,
}: {
  entity: EntityData | null;
  label: string;
  truncate?: boolean;
}) {
  return entity ? (
    <EntityTag entity={entity} interactive={false} size="sm" />
  ) : (
    <Text size="sm" truncate={truncate}>
      {label}
    </Text>
  );
}

export function FormOptionChip({ entity, label }: { entity: EntityData | null; label: string }) {
  return entity ? (
    <EntityTag entity={entity} interactive={false} size="xs" />
  ) : (
    <Badge tone="outline">{label}</Badge>
  );
}

/** A kind chip (type, stage, source) is the answer and nothing more: its detail dialog is a door into settings, not this submission. */
export function FormAnswerValue({
  property,
  value,
  fallback,
}: {
  property: FormProperty | null;
  value: unknown;
  fallback: ReactNode;
}) {
  const entityOf = useFormOptionEntity(property);
  const entities = Array.isArray(value)
    ? value.flatMap((id) => {
        const entity = typeof id === "string" ? entityOf(id) : null;
        return entity ? [entity] : [];
      })
    : [];
  const [only, ...rest] = entities;
  if (!only) return fallback;
  return (
    <Box align="center" direction="row">
      {rest.length === 0 ? (
        <EntityTag entity={only} interactive={hasEntityPreview(only)} size="sm" />
      ) : (
        <EntityGroupTag entities={entities} />
      )}
    </Box>
  );
}
